// Material
import { useCallback } from 'react'
// Material
import { styled } from '@mui/material/styles'
import { Box, InputBase, InputBaseComponentProps, SxProps } from '@mui/material'
// Component
import { LengthVerifier } from '$common/input/length-verifier'
import { Alert } from './alert'

type Size = 'small' | 'medium'

const StyledInputBase = styled(InputBase)<{ size: Size }>`
  background-color: ${({ theme }) => theme.palette.neutral000};
  padding: 4px 14px 3px;
  border-radius: 12px;
  ${({ theme }) => ({ ...theme.typography.paragraph2 })};
  width: 100%;
  height: ${({ size }) => (size === 'small' ? '40px' : '48px')};
  color: ${({ theme }) => theme.palette.neutral900};
  border: 1px solid ${({ theme }) => theme.palette.neutral300};
  box-sizing: border-box;
  .MuiInput-inputAdornedStart {
    margin-right: 20px;
  }
  &:hover {
    background-color: ${({ theme }) => theme.palette.primary50};
  }
  &.Mui-focused {
    background-color: ${({ theme }) => theme.palette.neutral000};
    border: 2px solid ${({ theme }) => theme.palette.primary300};
    padding: 4px 13px 3px;
    caret-color: ${({ theme }) => theme.palette.primary300};
  }
  &.Mui-disabled {
    background-color: ${({ theme }) => theme.palette.neutral300};
    color: ${({ theme }) => theme.palette.neutral400};
  }
  &.Mui-error {
    border-color: ${({ theme }) => theme.palette.error.main};
  }
`

interface Props {
  placeholder: string
  onChange: (text: string) => void
  value: string
  successMessage?: string
  errorMessage?: string
  direction?: 'ltr' | 'rtl' | 'inherit'
  hasError?: boolean
  autoComplete?: string
  onBlur?: () => unknown
  maxLength?: number
  disableLengthError?: boolean
  showMaxLength?: boolean
  disabled?: boolean
  startAdornment?: JSX.Element
  inputSx?: SxProps
  inputProps?: InputBaseComponentProps
  size?: Size
  rootSx?: SxProps
}

export function TextInput({
  placeholder,
  value,
  onChange,
  successMessage = '',
  errorMessage = '',
  direction = 'inherit',
  hasError = false,
  autoComplete = '',
  onBlur,
  maxLength,
  disableLengthError = false,
  showMaxLength = false,
  disabled = false,
  startAdornment,
  inputSx,
  inputProps,
  size = 'medium',
  rootSx,
}: Props) {
  const handleInputChange = useCallback(
    (newValue: string) => {
      if (!maxLength || newValue.length <= maxLength) onChange(newValue)
    },
    [maxLength, onChange],
  )

  return (
    <Box
      sx={{
        marginTop: '8px',
        marginBottom: '16px',
        width: '100%',
        ...rootSx,
      }}
    >
      <StyledInputBase
        size={size}
        startAdornment={startAdornment}
        placeholder={placeholder}
        onChange={({ target: { value: textValue } }) =>
          handleInputChange(textValue)
        }
        value={value}
        dir={direction}
        autoComplete={autoComplete}
        onBlur={onBlur}
        disabled={disabled}
        sx={inputSx}
        inputProps={inputProps}
      />

      {showMaxLength && maxLength && (
        <Box sx={{ marginTop: '8px' }}>
          <LengthVerifier
            value={value}
            maxLength={maxLength}
            disableErrorMode={disableLengthError}
          />
        </Box>
      )}

      {hasError && (
        <Alert
          variant={hasError ? 'error' : 'success'}
          text={hasError ? errorMessage : successMessage}
        />
      )}
    </Box>
  )
}
